import type { SlotInfo } from '../../../types/schedule.types';

/** 场次/跨日单卡插入缝、卡片半区 droppable 的 data.type */
export const SLOT_INSERT_GAP_DATA_TYPE = 'slotInsertGap' as const;
export const SLOT_INSERT_CARD_HALF_DATA_TYPE = 'slotInsertCardHalf' as const;

export type SlotInsertDropData = {
  type: typeof SLOT_INSERT_GAP_DATA_TYPE | typeof SLOT_INSERT_CARD_HALF_DATA_TYPE;
  dateStr: string;
  insertIndex: number;
};

/**
 * 构建插入位 droppable id（同一 insertIndex 可多实例，解析以 data 为准）
 */
export function buildSlotInsertDroppableId(
  dateStr: string,
  insertIndex: number,
  slot: 'gap' | 'cardTop' | 'cardBot',
  rowIndex?: number,
): string {
  if (slot === 'gap') {
    return `slot-insert__${dateStr}__${insertIndex}__gap`;
  }
  return `slot-insert__${dateStr}__${insertIndex}__${slot}-${rowIndex ?? 0}`;
}

/**
 * 从 over.data 解析插入位（优先）；id 仅作后备
 */
export function parseSlotInsertFromOver(
  overId: string,
  overData?: { type?: string; dateStr?: string; insertIndex?: number } | null,
): { dateStr: string; insertIndex: number } | null {
  const t = overData?.type;
  if (
    (t === SLOT_INSERT_GAP_DATA_TYPE || t === SLOT_INSERT_CARD_HALF_DATA_TYPE) &&
    overData?.dateStr &&
    /^\d{4}-\d{2}-\d{2}$/.test(overData.dateStr) &&
    typeof overData.insertIndex === 'number' &&
    Number.isFinite(overData.insertIndex) &&
    overData.insertIndex >= 0
  ) {
    return { dateStr: overData.dateStr, insertIndex: overData.insertIndex };
  }
  const idStr = overId.toString();
  if (idStr.startsWith('slot-insert__')) {
    const parts = idStr.split('__');
    if (
      parts.length >= 4 &&
      parts[0] === 'slot-insert' &&
      /^\d{4}-\d{2}-\d{2}$/.test(parts[1] ?? '')
    ) {
      const idx = parseInt(parts[2] ?? '', 10);
      if (!Number.isNaN(idx) && idx >= 0) {
        return { dateStr: parts[1], insertIndex: idx };
      }
    }
  }
  return null;
}

/**
 * 外部插入落点：显式插入位优先；否则仅有 calendar-day / slot 等时按目标日行数落到末尾（与拍摄计划约定一致）
 */
export function resolveInsertIndexForExternalDrop(
  overId: string,
  overData: unknown,
  optimisticSlots: SlotInfo[],
  rowCountOnTargetDate: number,
): { dateStr: string; insertIndex: number } | null {
  const parsed = parseSlotInsertFromOver(overId.toString(), overData as SlotInsertDropData | null);
  if (parsed) {
    const clamped = Math.max(0, Math.min(rowCountOnTargetDate, parsed.insertIndex));
    return { dateStr: parsed.dateStr, insertIndex: clamped };
  }
  const dateStr = extractDateFromOverId(
    overId.toString(),
    overData as { dateStr?: string } | null,
    optimisticSlots,
  );
  if (!dateStr) return null;
  return { dateStr, insertIndex: rowCountOnTargetDate };
}

/**
 * 从 over.id 或 over.data 中提取日期字符串
 * 支持：calendar-day-YYYY-MM-DD、wholeDay-YYYY-MM-DD、dateStr__segment-xxx、dateStr__note-xxx、slotID、note-xxx
 */
export function extractDateFromOverId(
  overId: string,
  overData?: { dateStr?: string } | null,
  optimisticSlots: SlotInfo[] = []
): string | null {
  const idStr = overId.toString();

  if (overData?.dateStr && /^\d{4}-\d{2}-\d{2}$/.test(overData.dateStr)) {
    return overData.dateStr;
  }

  const fromInsert = parseSlotInsertFromOver(idStr, overData as SlotInsertDropData | null);
  if (fromInsert) return fromInsert.dateStr;

  if (idStr.startsWith('calendar-day-')) {
    const rest = idStr.replace('calendar-day-', '');
    const dateMatch = rest.match(/^(\d{4}-\d{2}-\d{2})/);
    return dateMatch ? dateMatch[1] : rest;
  }
  if (idStr.startsWith('wholeDay-')) {
    const rest = idStr.replace('wholeDay-', '');
    if (/^\d{4}-\d{2}-\d{2}$/.test(rest)) return rest;
  }
  if (idStr.includes('__')) {
    const prefix = idStr.split('__')[0];
    if (prefix && /^\d{4}-\d{2}-\d{2}$/.test(prefix)) return prefix;
  }

  const slot = optimisticSlots.find((slot) => slot.slotID === idStr);
  if (slot?.slotDate) {
    return slot.slotDate.split('T')[0];
  }
  if (idStr.startsWith('note-')) {
    const slotID = idStr.replace('note-', '');
    const pt = optimisticSlots.find((t) => t.slotID === slotID);
    if (pt?.slotDate) return pt.slotDate.split('T')[0];
  }
  return null;
}

/**
 * 检查 slot 是否重复（同一 sceneID + dateStr 下是否已有任务）
 */
export function checkSlotDuplicate(
  sceneID: string,
  dateStr: string,
  excludeSlotID: string | undefined,
  scheduleIDParam: string | undefined,
  optimisticSlots: SlotInfo[]
): boolean {
  return optimisticSlots.some((slot) => {
    if (excludeSlotID && slot.slotID === excludeSlotID) {
      return false;
    }

    const slotDateStr = slot.slotDate?.split('T')[0];

    if (scheduleIDParam !== undefined) {
      return (
        slot.slotBelongSceneID === sceneID &&
        slot.slotBelongScheduleID === scheduleIDParam &&
        slotDateStr === dateStr
      );
    }
    return slot.slotBelongSceneID === sceneID && slotDateStr === dateStr;
  });
}
